import React, { type ChangeEvent, forwardRef, useCallback, useState, useRef } from "react";
import { RapButton } from "@dynata/rex-raptor";
import "../../../../scss/manage-users.scss";
import type {
    OrganizationData,
    UpdateUserRequest,
} from "../../../../services/management-backend/client";
import { multiAccountRoles, organizationLevelRoles, singleAccountRoles } from "../manageUsers.page";
import { Popup } from "../../../../core/widgets/popup.widget";
import { RiErrorWarningLine } from "react-icons/ri";
import User from "models/user";
import { Role, roleToUserRoleAssignment } from "models/user";
import Account from "models/account";

// Constants to store reused strings in the code.
const INACTIVE = "Inactive";
const ADMIN = "Admin";
const ORGANIZATION_OWNER = "Organization Owner";

export type EditPopupProps = {
    onClose: () => void;
    user: User;
    onUpdateUserClick: (body: UpdateUserRequest) => void;
    onDeleteUserClick: () => void;
    organizationAccounts: Account[]
    organizationData: OrganizationData
};

const EditUserPopup = forwardRef<HTMLDialogElement, EditPopupProps>(
    (
        { onClose, user, onUpdateUserClick, onDeleteUserClick, organizationAccounts, organizationData }: EditPopupProps,
        ref: React.ForwardedRef<HTMLDialogElement>,
    ) => {
        const [formValues, setFormValues] = useState<Omit<Role, "accountName" | "accountNumber" | "isAdhoc">[]>(user.roles);

        const [formState, setFormState] = useState({
            formSubmitted: false,
            isOrganizationAdmin: user.isAdmin, // Boolean
            isAccountRolesLocked: user.isAdmin && organizationAccounts.length > 1, // Boolean
            isPopupOpen: false,
        })

        const modalRef = useRef<HTMLDialogElement>(null);

        const handleSelectChange = useCallback((id: number, account: string, event: ChangeEvent<HTMLSelectElement>) => {
            setFormValues((prevState) => {
                const role = event.target.value;
                if (role === "Inactive") {
                    return prevState.filter((value) => value.accountId !== id)
                }
                else if (role === "Organization Owner") {
                    // Organization owners get set to the "Admin" role AND is_organization_admin = true.
                    // Regardless if it is a single or multi-account organization.
                    setFormState((prevState) =>
                        ({ ...prevState, isOrganizationAdmin: true }));
                    const updateState = prevState.filter((value) => value.accountId !== id)
                    return [...updateState, { accountId: id, role: "Admin" }]
                }
                else if (organizationAccounts.length > 1) {
                    // For multi-account organizations - do not change the admin flag from the role select options.
                    const updateState = prevState.filter((value) => value.accountId !== id)
                    return [...updateState, { accountId: id, role: role }]
                }
                else {
                    // For single-account organizations - make sure to update the admin flag to false for anything other than "Organization Owner".
                    setFormState((prevState) =>
                        ({ ...prevState, isOrganizationAdmin: false }));
                    const updateState = prevState.filter((value) => value.accountId !== id)
                    return [...updateState, { accountId: id, role: role }]
                }
            })
        }, [organizationAccounts.length]);

        // Sets all the account data roles to Admin.
        const setAllAccountsToAdmin = useCallback(() => {
            setFormValues(organizationAccounts.map(({ id }) => ({ accountId: id, role: ADMIN })));
        }, [organizationAccounts]);

        const handleOrganizationSelectChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
            const role = event.target.value;
            if (role === "Organization Owner") {
                setFormState((prevState) =>
                    ({ ...prevState, isAccountRolesLocked: true, isOrganizationAdmin: true }));
                setAllAccountsToAdmin();
            } else {
                setFormState((prevState) =>
                    ({ ...prevState, isAccountRolesLocked: false, isOrganizationAdmin: false }));
            }
        }, [])

        const onSubmitForm = useCallback((event: React.FormEvent) => {
            event.preventDefault();
            setFormState((prevState) =>
                ({ ...prevState, formSubmitted: true }));
            if (!formValues.some(({ role }) => role !== INACTIVE)) {
                return;
            }
            onUpdateUserClick({ accounts: formValues.map(roleToUserRoleAssignment), is_organization_admin: formState.isOrganizationAdmin });
        }, [formValues, formState, onUpdateUserClick])

        const handleDeleteClick = useCallback(() => {
            setFormState((prevState) =>
                ({ ...prevState, isPopupOpen: true }));
            modalRef.current?.showModal();
        }, []);

        const handleConfirmDelete = useCallback(() => {
            setFormState((prevState) =>
                ({ ...prevState, isPopupOpen: false }));
            modalRef.current?.close();
            onDeleteUserClick();
        }, [onDeleteUserClick]);

        const handleCancelDelete = useCallback(() => {
            setFormState((prevState) =>
                ({ ...prevState, isPopupOpen: false }));
            modalRef.current?.close();
        }, []);

        return (
            <dialog className="manageUser_popup_wrapper overlay" ref={ref}>
                <div className="model-popup-container">
                    <div className="edit-popup-container" >
                        <div>
                            <span className="label-title">
                                {user.firstName} {user.lastName} ({user.username})
                            </span>
                        </div>
                        <span className="label-title">Organization</span>

                        {/* IF organization is multi-account -> Then display organization level role selection */}
                        {organizationAccounts.length > 1 && (
                            <>
                                <div className={"flex input-container"}>
                                    <label className="dark_mode_wrapper" htmlFor="organization-select">{organizationData?.display_name}</label>
                                    <select
                                        className="dark_mode_wrapper"
                                        id="organization-select"
                                        name="organization-select"
                                        defaultValue={user.isAdmin ? "Organization Owner" : "Contributor"}
                                        onChange={(event) => {
                                            handleOrganizationSelectChange(event)
                                        }}
                                    >
                                        {organizationLevelRoles.map((role, index) => (
                                            <option key={index} value={role}>{role}</option>
                                        ))}
                                    </select>
                                </div>
                                <span className="label-title">Accounts</span>

                                {/* Show error label for Account Role missing for multi-account */}
                                {formState.formSubmitted && !formValues.some(({ role }) => role !== INACTIVE) && <label className="error-label">* At least one Account Role selection is required</label>}
                            </>
                        )}
                        <form className="edit-popup-form" onSubmit={onSubmitForm}>
                            {organizationAccounts.map(({ name, id }, index) => (
                                <div key={index} className={"flex input-container"}>
                                    <label className="dark_mode_wrapper" htmlFor={`dropdown-${id}`}>{name}</label>
                                    <select
                                        className="dark_mode_wrapper"
                                        id={`dropdown-${id}`}
                                        name={`dropdown-${id}`}
                                        onChange={(event) => {
                                            handleSelectChange(id, name, event)
                                        }}
                                        // If user is an organization admin - display the role as Organization Owner.
                                        value={organizationAccounts.length <= 1 && formState.isOrganizationAdmin ? ORGANIZATION_OWNER : formValues.find(value => value.accountId === id)?.role || (INACTIVE)}
                                        disabled={formState.isAccountRolesLocked}>
                                        {organizationAccounts.length > 1 ? multiAccountRoles.map((value, index) => (
                                            <option key={value} value={value}>{value}</option>
                                        )) : singleAccountRoles.map((value, index) => (
                                            <option key={value} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                            <div className="flex btn-container">
                                <div>
                                    <RapButton type="button" color="danger" onClick={handleDeleteClick}>Delete</RapButton>
                                </div>
                                <div className="right-btn-section">
                                    <RapButton type="button" styleType="secondary" color="alternative" onClick={onClose}>Close</RapButton>
                                    <RapButton type="submit" styleType="primary">Update</RapButton>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>


                <Popup
                    ref={modalRef}
                    description={
                        <>
                            Are you sure you want to delete this user <strong>{user.username}</strong>?
                        </>
                    }
                    onAccept={handleCancelDelete}
                    onAcceptMessage="Cancel"
                    onReject={handleConfirmDelete}
                    onRejectMessage="Confirm"
                    icon={<RiErrorWarningLine />}
                />
            </dialog>
        );
    },
);

export default EditUserPopup;
